import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  LinearProgress,
  IconButton
} from '@mui/material';
import WaterCard from './WaterCard';
import {
  ArrowBack,
  Phone,
  AccessTime,
  Person,
  Category,
  CheckCircle,
  Warning,
  Star,
  Assessment,
  RecordVoiceOver,
  PlayArrow,
  CallMade,
  CompareArrows,
  ConfirmationNumber
} from '@mui/icons-material';
import { callsApi } from '../services/api';
import PCADashboard from './PCADashboard';
import CallbackManager from './CallbackManager';
import CallNotes from './CallNotes';

const CallDetailPage: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (callId) {
      fetchCallDetails();
    }
  }, [callId]);

  const fetchCallDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await callsApi.getCallById(callId!);
      setCall(response.data.call);
    } catch (err: any) {
      console.error('Error fetching call details:', err);
      setError(err.response?.data?.error || 'Failed to load call details');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds || seconds <= 0) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return 'N/A';
    
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    return phone;
  };

  const detectTransferInfo = (transcript?: string) => {
    if (!transcript) return null;
    
    const transferKeywords = [
      'transfer target:',
      'transferring',
      'transferred',
      'warm transfer',
      'transfer to',
      'connecting you to',
      'putting you through to',
      'let me transfer you',
      'i\'ll transfer you',
      'connecting you with',
      'activation team'
    ];
    
    const lines = transcript.split('\n');
    let hasTransfer = false;
    let transferTarget = 'Support Team';
    let transferPhone = null;
    
    for (const line of lines) {
      const trimmed = line.trim().toLowerCase();
      const hasTransferKeyword = transferKeywords.some(keyword => trimmed.includes(keyword));
      
      if (hasTransferKeyword) {
        hasTransfer = true;
        
        const phoneMatch = line.match(/\+?1?\s*\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})/);
        if (phoneMatch) {
          transferPhone = `+1 (${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
        }
        
        if (line.match(/activation team|activation|setup team/i)) {
          transferTarget = 'Activation Team';
        } else if (line.match(/billing|payment|account balance/i)) {
          transferTarget = 'Billing Department';
        } else if (line.match(/technical support|tech support|technician/i)) {
          transferTarget = 'Technical Support';
        } else if (line.match(/retention|cancel|disconnect/i)) {
          transferTarget = 'Retention Team';
        } else if (line.match(/sales|upgrade/i)) {
          transferTarget = 'Sales Department';
        }
      }
    }
    
    if (!hasTransfer) return null;
    
    return {
      transferred: true,
      target: transferTarget,
      phone: transferPhone,
      type: 'Warm Transfer'
    };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'N/A';
    }
  };

  const formatTranscript = (transcript?: string) => {
    if (!transcript) return null;
    
    const customerName = call?.customer_name || 'Customer';
    const lines = transcript.split('\n').filter(line => line.trim());
    
    interface Message {
      speaker: 'fona' | 'customer' | 'system' | 'transfer';
      text: string;
      transferInfo?: {
        target?: string;
        phone?: string | null;
        type?: string;
      };
    }
    
    const messages: Message[] = [];
    let currentMessage: Message | null = null;
    
    lines.forEach((line) => {
      const trimmed = line.trim();
      
      const isTransferTargetLine = trimmed.startsWith('Transfer Target:');
      
      if (isTransferTargetLine) {
        if (trimmed.match(/transfer target:\s*(hi|hello|yes|hey)/i)) {
          return;
        }
        
        if (currentMessage) {
          messages.push(currentMessage);
          currentMessage = null;
        }
        
        let transferTarget = 'Support Team';
        let transferPhone = null;
        
        const phoneMatch = line.match(/\+?1?\s*\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})/);
        if (phoneMatch) {
          transferPhone = `+1 (${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
        }
        
        const recentText = messages.slice(-3).map(m => m.text).join(' ').toLowerCase();
        if (recentText.match(/activation team|activation/i)) {
          transferTarget = 'Activation Team';
        } else if (recentText.match(/billing|payment/i)) {
          transferTarget = 'Billing Department';
        } else if (recentText.match(/technical support|tech support/i)) {
          transferTarget = 'Technical Support';
        } else if (recentText.match(/retention|cancel/i)) {
          transferTarget = 'Retention Team';
        } else if (recentText.match(/sales|upgrade/i)) {
          transferTarget = 'Sales Department';
        }
        
        messages.push({
          speaker: 'transfer',
          text: `Call transferred to ${transferTarget}${transferPhone ? ` (${transferPhone})` : ''}`,
          transferInfo: {
            target: transferTarget,
            phone: transferPhone,
            type: 'warm'
          }
        });
        return;
      }
      
      if (trimmed.startsWith('Agent:') || trimmed.startsWith('Fona:') || trimmed.startsWith('AI:')) {
        const messageText = trimmed.replace(/^(Agent|Zoe|AI):\s*/i, '').trim();
        
        if (currentMessage && currentMessage.speaker === 'fona') {
          currentMessage.text += (currentMessage.text ? ' ' : '') + messageText;
        } else {
          if (currentMessage) messages.push(currentMessage);
          currentMessage = {
            speaker: 'fona',
            text: messageText
          };
        }
      } 
      else if (trimmed.startsWith('User:') || trimmed.startsWith('Customer:') || trimmed.startsWith('Caller:')) {
        const messageText = trimmed.replace(/^(User|Customer|Caller):\s*/i, '').trim();
        
        if (currentMessage && currentMessage.speaker === 'customer') {
          currentMessage.text += (currentMessage.text ? ' ' : '') + messageText;
        } else {
          if (currentMessage) messages.push(currentMessage);
          currentMessage = {
            speaker: 'customer',
            text: messageText
          };
        }
      }
      else if (trimmed.includes(':') && trimmed.indexOf(':') < 50) {
        const colonIndex = trimmed.indexOf(':');
        const speaker = trimmed.substring(0, colonIndex).trim();
        const messageText = trimmed.substring(colonIndex + 1).trim();
        
        if (speaker.toLowerCase() === 'user' || 
            speaker.toLowerCase() === 'customer' || 
            speaker.toLowerCase() === 'caller' ||
            speaker.toLowerCase().includes('user') ||
            speaker.toLowerCase().includes('customer') ||
            speaker.toLowerCase().includes('caller')) {
          if (currentMessage && currentMessage.speaker === 'customer') {
            currentMessage.text += (currentMessage.text ? ' ' : '') + messageText;
          } else {
            if (currentMessage) messages.push(currentMessage);
            currentMessage = {
              speaker: 'customer',
              text: messageText
            };
          }
        } 
        else if (speaker.toLowerCase() === 'agent' || 
                 speaker.toLowerCase() === 'fona' ||
                 speaker.toLowerCase().includes('agent') ||
                 speaker.toLowerCase().includes('fona')) {
          if (currentMessage && currentMessage.speaker === 'fona') {
            currentMessage.text += (currentMessage.text ? ' ' : '') + messageText;
          } else {
            if (currentMessage) messages.push(currentMessage);
            currentMessage = {
              speaker: 'fona',
              text: messageText
            };
          }
        } else {
          if (currentMessage) {
            currentMessage.text += ' ' + trimmed;
          } else {
            if (currentMessage) messages.push(currentMessage);
            currentMessage = {
              speaker: 'system',
              text: trimmed
            };
          }
        }
      } 
      else if (trimmed) {
        if (currentMessage) {
          currentMessage.text += (currentMessage.text ? ' ' : '') + trimmed;
        } else {
          if (currentMessage) messages.push(currentMessage);
          currentMessage = {
            speaker: 'system',
            text: trimmed
          };
        }
      }
    });
    
    if (currentMessage) messages.push(currentMessage);
    
    // Clean, simple chat view
    return (
      <Stack spacing={2}>
        {messages.map((msg, index) => {
          if (msg.speaker === 'fona') {
            return (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box
                  sx={{
                    maxWidth: '75%',
                    backgroundColor: '#F0F4FF',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid #E0E7FF'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#3B82F6', mb: 0.5, display: 'block' }}>
                    Fona (AI Agent)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1E293B', lineHeight: 1.6 }}>
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            );
          } else if (msg.speaker === 'customer') {
            return (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box
                  sx={{
                    maxWidth: '75%',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', mb: 0.5, display: 'block' }}>
                    {customerName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1E293B', lineHeight: 1.6 }}>
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            );
          } else if (msg.speaker === 'transfer') {
            return (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#ECFDF5',
                    border: '1px solid #A7F3D0',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    maxWidth: '85%',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CompareArrows sx={{ color: '#059669', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#065F46' }}>
                        {msg.text}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
            );
          } else {
            return (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
                  {msg.text}
                </Typography>
              </Box>
            );
          }
        })}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!call) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Call not found
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const duration = call.duration_seconds || call.duration || null;
  const aiConfidence = parseFloat(call.ai_confidence || 0);
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Simple Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid #E2E8F0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            variant="outlined"
            sx={{ borderColor: '#E2E8F0', color: '#64748B' }}
          >
            Back to Dashboard
          </Button>
          
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {call.channel_type && (
              <Chip
                label={call.channel_type}
                size="small"
                sx={{
                  backgroundColor: call.channel_type === 'CHAT' ? '#DCFCE7' : '#DBEAFE',
                  color: call.channel_type === 'CHAT' ? '#166534' : '#1E40AF',
                  fontWeight: 600
                }}
              />
            )}
            {call.resolved && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
                label="Resolved"
                size="small"
                sx={{
                  backgroundColor: '#DCFCE7',
                  color: '#166534',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: '#166534' }
                }}
              />
            )}
          </Stack>
        </Box>
        
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
          Call Details
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
          {call.call_id || callId}
        </Typography>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Post-Call Analysis */}
          {call.issue_category && (
            <WaterCard sx={{ mb: 3, border: 1, borderColor: 'divider', boxShadow: 'none' }}>
              <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A' }}>
                  Post-Call Analysis
                </Typography>
              </Box>
              <CardContent>
                <PCADashboard
                  pcaData={{
                    customerName: call.customer_name || 'Unknown',
                    issueCategory: call.issue_category || 'N/A',
                    issueDetails: call.issue_details || 'No details available',
                    troubleshootingSteps: call.troubleshooting_steps || null,
                    resolutionStatus: call.resolution_status || (call.resolved ? 'Resolved' : 'Pending'),
                    urgencyLevel: call.urgency_level || 'Low',
                    aiConfidence: aiConfidence,
                    callId: call.call_id || callId || undefined,
                    duration: duration
                  }}
                  callMetadata={{
                    startTime: call.start_time,
                    endTime: call.end_time,
                    duration: duration
                  }}
                />
              </CardContent>
            </WaterCard>
          )}

          {/* Transcript */}
          <WaterCard sx={{ mb: 3, border: 1, borderColor: 'divider', boxShadow: 'none' }}>
            <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A' }}>
                Conversation Transcript
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {call.transcript ? (
                <Box 
                  sx={{ 
                    maxHeight: '600px', 
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#F1F5F9',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#CBD5E1',
                      borderRadius: '4px',
                    },
                  }}
                >
                  {formatTranscript(call.transcript)}
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    No transcript available for this call.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </WaterCard>

          {/* Recording */}
          {call.recording_url && (
            <WaterCard sx={{ border: 1, borderColor: 'divider', boxShadow: 'none' }}>
              <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A' }}>
                  Call Recording
                </Typography>
              </Box>
              <CardContent>
                <audio controls style={{ width: '100%' }}>
                  <source src={call.recording_url} type="audio/wav" />
                  Your browser does not support the audio element.
                </audio>
                {duration && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Duration: {formatDuration(duration)}
                  </Typography>
                )}
              </CardContent>
            </WaterCard>
          )}
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid item xs={12} md={4}>
          <WaterCard sx={{ border: 1, borderColor: 'divider', boxShadow: 'none' }}>
            <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A' }}>
                Call Information
              </Typography>
            </Box>
            <CardContent>
              <Stack spacing={3}>
                {/* Customer */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    Customer
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                    {call.customer_name || 'Unknown'}
                  </Typography>
                </Box>

                {/* Phone */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A', fontFamily: 'monospace' }}>
                    {formatPhoneNumber(call.customer_phone)}
                  </Typography>
                </Box>

                {/* Date & Time */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                    {formatDate(call.start_time || call.created_at)}
                  </Typography>
                </Box>

                {/* Duration */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    Duration
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                    {formatDuration(duration)}
                  </Typography>
                </Box>

                {/* Category */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    Category
                  </Typography>
                  <Chip
                    label={call.issue_category || 'N/A'}
                    size="small"
                    sx={{
                      backgroundColor: '#F1F5F9',
                      color: '#475569',
                      fontWeight: 600
                    }}
                  />
                </Box>

                {/* Status */}
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                    Status
                  </Typography>
                  <Chip
                    label={call.resolution_status || (call.resolved ? 'Resolved' : 'Pending')}
                    size="small"
                    sx={{
                      backgroundColor: call.resolved ? '#DCFCE7' : '#FEF3C7',
                      color: call.resolved ? '#166534' : '#92400E',
                      fontWeight: 600
                    }}
                  />
                </Box>

                {/* Zendesk Ticket */}
                {call.ticket_id && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                      Zendesk Ticket
                    </Typography>
                    <Chip
                      size="small"
                      icon={<ConfirmationNumber />}
                      label={`Zendesk ticket #${call.ticket_id}`}
                      component="a"
                      href={`https://fongo.zendesk.com/agent/tickets/${call.ticket_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                    />
                  </Box>
                )}

                {/* AI Confidence */}
                {aiConfidence > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                      AI Confidence
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
                      {(aiConfidence * 100).toFixed(1)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={aiConfidence * 100}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: '#F1F5F9',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: aiConfidence > 0.8 ? '#10B981' : aiConfidence > 0.6 ? '#3B82F6' : '#F59E0B',
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Transfer Info */}
                {(() => {
                  const transferInfo = detectTransferInfo(call.transcript);
                  if (transferInfo) {
                    return (
                      <Box sx={{ pt: 2, borderTop: '1px solid #E2E8F0' }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                          Transfer Info
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                            {transferInfo.type}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748B' }}>
                            → {transferInfo.target}
                          </Typography>
                          {transferInfo.phone && (
                            <Typography variant="body2" sx={{ color: '#64748B', fontFamily: 'monospace' }}>
                              {transferInfo.phone}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Stack>
            </CardContent>
          </WaterCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CallDetailPage;
